/**
 * useGridSelection - Uncontrolled selection management for OmnisearchDataGridV2.
 *
 * ARCHITECTURE:
 * - Grid manages selection INTERNALLY (no rowSelectionModel prop)
 * - Provider creates and owns the apiRef
 * - Grid auto-wires to provider's apiRef via context
 * - Selection consumers subscribe via useGridSelection() hook
 * - Parent components NEVER re-render on selection change
 *
 * USAGE:
 * ```tsx
 * function MyPage() {
 *   return (
 *     <GridSelectionProvider>
 *       <BulkActionsMenu />
 *       <OmnisearchDataGridV2 checkboxSelection />
 *     </GridSelectionProvider>
 *   );
 * }
 *
 * function BulkActionsMenu() {
 *   const { selectedCount, getSelectedRows, clearSelection } = useGridSelection();
 *   return (
 *     <Button disabled={selectedCount === 0} onClick={() => {
 *       const rows = getSelectedRows();
 *       doSomething(rows);
 *       clearSelection();
 *     }}>
 *       Bulk Actions
 *     </Button>
 *   );
 * }
 * ```
 */

import {
  type GridRowId,
  type GridValidRowModel,
  type GridApiPremium,
  useGridApiRef,
} from "@mui/x-data-grid-premium";
import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";

// =============================================================================
// Context
// =============================================================================

interface GridSelectionContextValue {
  apiRef: React.RefObject<GridApiPremium | null>;
}

const GridSelectionContext = createContext<GridSelectionContextValue | null>(null);

// =============================================================================
// Provider
// =============================================================================

interface GridSelectionProviderProps {
  children: ReactNode;
}

/**
 * Provider for grid selection state. Wrap around grid and selection consumers.
 *
 * The provider creates the apiRef internally - consumers don't need to know
 * about apiRef at all.
 */
export function GridSelectionProvider({ children }: GridSelectionProviderProps) {
  const apiRef = useGridApiRef();

  return (
    <GridSelectionContext.Provider value={{ apiRef }}>{children}</GridSelectionContext.Provider>
  );
}

// =============================================================================
// Hook to get apiRef (for grid internal use)
// =============================================================================

/**
 * Get the apiRef from context. Used internally by OmnisearchDataGridV2.
 * Returns null if not inside a GridSelectionProvider.
 */
export function useGridSelectionApiRef(): React.RefObject<GridApiPremium | null> | null {
  const context = useContext(GridSelectionContext);
  return context?.apiRef ?? null;
}

// =============================================================================
// Types
// =============================================================================

export interface UseGridSelectionReturn<TRow extends GridValidRowModel = GridValidRowModel> {
  /**
   * Number of selected items. Reactive - causes re-render when selection changes.
   * Use this for enabling/disabling bulk action buttons.
   */
  selectedCount: number;

  /**
   * Get selected row IDs. Non-reactive - call when you need the IDs.
   * Does NOT cause re-renders.
   */
  getSelectedIds: () => GridRowId[];

  /**
   * Get selected rows with full data. Non-reactive - call when you need the rows.
   * Does NOT cause re-renders.
   */
  getSelectedRows: () => TRow[];

  /**
   * Clear all selections. Stable callback.
   */
  clearSelection: () => void;

  /**
   * Check if a specific ID is selected.
   */
  isSelected: (id: GridRowId) => boolean;
}

// =============================================================================
// Hook
// =============================================================================

/**
 * Hook for subscribing to grid selection state.
 *
 * Must be used inside a GridSelectionProvider.
 * Only components using this hook re-render on selection change.
 *
 * @returns Selection state and actions
 */
export function useGridSelection<
  TRow extends GridValidRowModel = GridValidRowModel,
>(): UseGridSelectionReturn<TRow> {
  const context = useContext(GridSelectionContext);

  if (!context) {
    throw new Error("useGridSelection must be used inside a GridSelectionProvider");
  }

  const { apiRef } = context;

  // Local state for count - only this component re-renders when selection changes
  const [selectedCount, setSelectedCount] = useState(0);

  // Subscribe to grid's internal selection events
  // We need to wait for the grid to mount before apiRef.current is available
  useEffect(() => {
    let unsubscribe: (() => void) | undefined;
    let mounted = true;
    let timerId: ReturnType<typeof setTimeout> | undefined;
    const startTime = Date.now();

    // Poll until apiRef.current is available (grid mounted). A Suspense-wrapped
    // grid mounts only after its first query resolves, which can far outlive
    // the old 5s deadline (slow network, cold cache) — giving up that early
    // left every selection consumer permanently inert, with bulk buttons that
    // never enabled. setTimeout rather than requestAnimationFrame so the poll
    // keeps running in hidden/background tabs, where browsers pause rAF
    // entirely (a page loaded in a background tab would otherwise never
    // attach). The generous deadline only bounds consumers mounted where a
    // grid never appears at all. Polling stops on unmount.
    const POLL_INTERVAL_MS = 100;
    const GIVE_UP_AFTER_MS = 60_000;
    const checkAndSubscribe = () => {
      // A tick can be in flight when the consumer unmounts (StrictMode's
      // setup→cleanup→setup especially); subscribing from it would leak a
      // grid listener nothing ever unsubscribes.
      if (!mounted) return;

      const api = apiRef.current;
      if (!api) {
        // Grid not mounted yet, try again shortly
        if (Date.now() - startTime < GIVE_UP_AFTER_MS) {
          timerId = setTimeout(checkAndSubscribe, POLL_INTERVAL_MS);
        }
        return;
      }

      // Update count immediately
      setSelectedCount(api.getSelectedRows().size);

      // Subscribe to row selection change events
      unsubscribe = api.subscribeEvent("rowSelectionChange", () => {
        setSelectedCount(api.getSelectedRows().size);
      });
    };

    checkAndSubscribe();

    return () => {
      mounted = false;
      if (timerId !== undefined) {
        clearTimeout(timerId);
      }
      unsubscribe?.();
    };
  }, [apiRef]);

  // Non-reactive getter for selected row IDs
  const getSelectedIds = useCallback((): GridRowId[] => {
    const api = apiRef.current;
    if (!api) return [];
    return Array.from(api.getSelectedRows().keys());
  }, [apiRef]);

  // Non-reactive getter for selected rows with full data
  const getSelectedRows = useCallback((): TRow[] => {
    const api = apiRef.current;
    if (!api) return [];
    return Array.from(api.getSelectedRows().values()) as TRow[];
  }, [apiRef]);

  // Clear selection via apiRef (does not cause parent re-renders)
  const clearSelection = useCallback(() => {
    const api = apiRef.current;
    if (!api) return;
    api.setRowSelectionModel({ type: "include", ids: new Set() });
  }, [apiRef]);

  // Check if specific ID is selected
  const isSelected = useCallback(
    (id: GridRowId): boolean => {
      const api = apiRef.current;
      if (!api) return false;
      return api.getSelectedRows().has(id);
    },
    [apiRef]
  );

  return {
    selectedCount,
    getSelectedIds,
    getSelectedRows,
    clearSelection,
    isSelected,
  };
}
